import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { collapse_chevron, filterRemark, sort_teal } from '../../../../assets/img';
import { AiCheckBox, BasicButton, BasicInput, BasicTable } from '../../../../assets/common';
import { paginationConfig } from '../../../../utils/helper';
import { getRemarksHistoricalApproved } from '../../../../services/successionplanningratio';
import { SORT_TYPE } from '../../../../utils/constants';
import * as styles from './all-remarks-approved.module.scss';
import { useSearchParams } from '../../../../hooks/useSearchQuery';

const INIT_SEARCH_PARAMS = {
  page: 1,
  searchText: '',
  birth_names: '',
  categories: '',
  platforms: '',
};

const CATEGORY_FILTERS = [
  'Strength',
  'Development Areas',
  'Notes',
  'Development Plan',
  'Next Mobility',
  'Next 2 Mobility',
  'End State',
];

const DEFAULT_LIMIT = 10;

const TitleSort = ({ title, type, setType }) => {
  const handleOnClick = () => {
    if (type === SORT_TYPE.ASC) {
      setType(SORT_TYPE.DESC);
    } else {
      setType(SORT_TYPE.ASC);
    }
  };
  return (
    <div className={styles.title}>
      <div>{title}</div>
      <button onClick={handleOnClick} onKeyDown={() => {}} className="print-ignore">
        <img
          src={sort_teal}
          alt="sort"
          style={{ transform: type === SORT_TYPE.DESC ? 'scaleY(-1)' : '' }}
        />
      </button>
    </div>
  );
};

const FilterIcon = () => <img src={filterRemark} alt="filter_icon" />;

const FilterDropdown = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  filterType,
  options,
  setSearchParams,
  visible,
}) => {
  const [tempSelected, setTempSelected] = useState([]);
  const handleApply = () => {
    setSelectedKeys(tempSelected);
    const filterParams = tempSelected.map((key) => key || 'null').join(',');
    setSearchParams((prev) => ({ ...prev, [filterType]: filterParams, resetPage: true }));
    confirm();
  };

  const handleClickCheckbox = (item) => {
    if (tempSelected.includes(item)) {
      setTempSelected(tempSelected.filter((key) => key !== item));
    } else {
      setTempSelected([...tempSelected, item]);
    }
  };

  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        setTempSelected([]);
      }, 300);
    } else {
      setTempSelected(selectedKeys);
    }
  }, [visible, selectedKeys]);

  return (
    <div className={styles.filter}>
      <div className={styles.checkboxList}>
        <div>
          {options.map((item) => (
            <div
              key={item}
              className={styles.checkbox}
              onClick={() => handleClickCheckbox(item)}
              onKeyDown={() => {}}
            >
              <AiCheckBox checked={tempSelected.includes(item)} />
              <span className={styles.label}>{item || 'null'}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.footer}>
        <BasicButton
          fontSize={10}
          mode="teal"
          height={20}
          round={50}
          padding="0 35px"
          onClick={handleApply}
          disabled={isEmpty(options)}
        >
          Apply
        </BasicButton>
      </div>
    </div>
  );
};

const AllRemarksApproved = ({ isPrint, printing, setPrinting, isCollapsed, setIsCollapsed }) => {
  const { positionCode } = useParams();
  const { agenda_id, approved_date, platform } = useSearchParams();
  const [searchParams, setSearchParams] = useState(INIT_SEARCH_PARAMS);
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState({ total: 0, remarks: [] });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    filterable_birth_names: [],
    filterable_platforms: [],
  });
  const [sortType, setSortType] = useState(SORT_TYPE.DESC);

  const fetchRemarks = useCallback(async () => {
    if (isPrint && (!printing || isCollapsed)) return;
    try {
      !isPrint && setLoading(true);
      const params = {
        ...searchParams,
        page: searchParams.resetPage ? 1 : searchParams.page,
        limit: !isPrint ? DEFAULT_LIMIT : undefined,
        sort_order: sortType,
        agenda_id: agenda_id,
        position_code: positionCode,
        is_historical_sp: Boolean(approved_date || platform || agenda_id),
        historical_platform: platform,
      };
      const res = await getRemarksHistoricalApproved(params);
      if (res.status === 200) {
        const resResult = res.data.result;
        setData({
          total: resResult.total,
          remarks: resResult.data.map((item, idx) => ({ ...item, id: idx })),
        });
        setFilters(resResult.metadata);
      }
    } catch (error) {
      console.error(error);
      setData({ total: 0, remarks: [] });
    } finally {
      setLoading(false);
      isPrint && setPrinting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda_id, isPrint, positionCode, printing, searchParams, setPrinting, sortType]);

  const handleInputSearch = (e) => {
    const value = e.target.value.trim();
    setSearchText(value);
  };

  const handleClickSearch = () => {
    setSearchParams((prev) => ({ ...prev, searchText, resetPage: true }));
  };

  useEffect(() => {
    fetchRemarks();
  }, [fetchRemarks]);

  const pagination = useMemo(() => {
    const onChange = (pageNum) => {
      setSearchParams((prevState) => ({ ...prevState, page: pageNum, resetPage: false }));
    };
    return paginationConfig(data.total, searchParams.page, onChange, DEFAULT_LIMIT);
  }, [data.total, searchParams.page, setSearchParams]);

  const columns = [
    {
      title: 'Name',
      width: 160,
      dataIndex: 'birth_name',
      render: (text) => <span className={styles.textField}>{text}</span>,
      filterIcon: <FilterIcon />,
      filterDropdown: (props) => (
        <FilterDropdown
          {...props}
          filterType="birth_names"
          options={filters.filterable_birth_names}
          setSearchParams={setSearchParams}
        />
      ),
    },
    {
      title: 'Category',
      width: 130,
      dataIndex: 'category',
      render: (text) => <span className={styles.textField}>{text}</span>,
      filterIcon: <FilterIcon />,
      filterDropdown: (props) => (
        <FilterDropdown
          {...props}
          filterType="categories"
          options={CATEGORY_FILTERS}
          setSearchParams={setSearchParams}
        />
      ),
    },
    {
      title: <TitleSort title="Date" type={sortType} setType={setSortType} />,
      width: 100,
      dataIndex: 'approved_date',
      render: (text) => (
        <span className={styles.textField}>{moment(text).format('DD MMM YYYY')}</span>
      ),
    },
    {
      title: 'Meeting Name',
      width: 200,
      dataIndex: 'meeting_name',
      render: (text) => <span className={styles.textField}>{text}</span>,
      filterIcon: <FilterIcon />,
      filterDropdown: (props) => (
        <FilterDropdown
          {...props}
          filterType="platforms"
          options={filters.filterable_platforms}
          setSearchParams={setSearchParams}
        />
      ),
    },
    {
      title: 'Remarks',
      width: 300,
      dataIndex: 'remark',
      render: (text) => <span className={styles.remarkField}>{text}</span>,
    },
  ];

  return (
    <div
      id="all-remarks-approved-print"
      className={styles.wrapper}
      style={{
        maxHeight: isCollapsed ? '84px' : '1500px',
        display: isPrint ? 'none' : '',
      }}
    >
      <button className={styles.header} onClick={() => setIsCollapsed((prev) => !prev)}>
        <div>All Remarks</div>
        <img
          src={collapse_chevron}
          alt="collapse"
          style={{ rotate: isCollapsed ? '180deg' : '0deg' }}
        />
      </button>
      <div className={styles.content} style={{ opacity: isCollapsed ? 0 : 100 }}>
        <div className={`${styles.searchBar} print-ignore`}>
          <BasicInput
            placeholder="Search Talent Name/ Talent ID"
            width={656}
            onBlur={handleInputSearch}
          />
          <BasicButton mode="teal" height={42} onClick={handleClickSearch}>
            Search
          </BasicButton>
        </div>
        <BasicTable
          rowKey={'id'}
          dataSource={data.remarks}
          columns={columns}
          pagination={isPrint ? false : pagination}
          loading={loading}
          styles={{ headerColor: '#3F3C4C', borderRadius: 6, headerWeight: 500 }}
        />
      </div>
    </div>
  );
};

export default AllRemarksApproved;
